/** Where the in-call settings panel sits. Pure geometry so it can be tested without a browser. */
export interface Box { left: number; top: number; width: number; height: number }
export interface Viewport { width: number; height: number }
export interface Anchor { left: number; top: number; right: number; bottom: number }

export const SHEET_BREAKPOINT = 640;
export const POPOVER_WIDTH = 380;
const MARGIN = 8;

/** Narrow screens and touch devices get a bottom sheet; wide pointer screens get a popover. */
export function usesSheet(viewportWidth: number, coarsePointer: boolean): boolean {
  return viewportWidth < SHEET_BREAKPOINT || coarsePointer;
}

/** Full-width bottom sheet, never taller than 85% of the viewport. */
export function sheetBox(viewport: Viewport, contentHeight: number): Box {
  const height = Math.min(contentHeight, Math.floor(viewport.height * 0.85));
  return { left: 0, top: viewport.height - height, width: viewport.width, height };
}

/**
 * Popover under the anchor, right edges aligned, flipped above when it does not fit below,
 * and shifted so that it never crosses any viewport edge.
 */
export function popoverBox(anchor: Anchor, contentHeight: number, viewport: Viewport): Box {
  const width = Math.min(POPOVER_WIDTH, viewport.width - 2 * MARGIN);
  const maxHeight = viewport.height - 2 * MARGIN;
  const height = Math.min(contentHeight, maxHeight);
  const left = Math.max(MARGIN, Math.min(anchor.right - width, viewport.width - width - MARGIN));
  const below = anchor.bottom + MARGIN;
  const fitsBelow = below + height <= viewport.height - MARGIN;
  const above = anchor.top - MARGIN - height;
  const preferred = fitsBelow ? below : above >= MARGIN ? above : below;
  const top = Math.max(MARGIN, Math.min(preferred, viewport.height - height - MARGIN));
  return { left, top, width, height };
}

export function insideViewport(box: Box, viewport: Viewport): boolean {
  return box.left >= 0 && box.top >= 0 && box.left + box.width <= viewport.width && box.top + box.height <= viewport.height;
}
